import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useEffect } from 'react';
import { ErrorWord, getMaxLengthErrorMessage, yupNumberString } from 'views/templates/personalAccountOpen/common';
import { useChangeDomesticRealEstateApplicationMutation, useGetAccountInfoQuery } from 'views/utils/graphql/graphql';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { AccountType } from 'views/templates/module/bankDetailsInput/hooks';
import { useGlobalLoadingHandler } from 'views/mutual/globalLoading/hook';
import { ChangeAccountInfoExecuteType, FatcaStatus } from 'views/utils/enums';
import { useAuthGuard } from 'views/auth/useAuth';

export type ApplicationStatusType = {
  bankName: string | null;
  branchName: string | null;
  accountType: AccountType | null;
  accountNumber: string | null;
  accountHolderName: string | null;
  fatca: FatcaStatus | null;
  invoiceNumber: string | null;
  hasNoInvoiceNumber: boolean | null;

  hasDividendAccountFlag: boolean;
  hasValidFatcaAnswerFlag: boolean;
  hasValidInvoiceNumberAnswerFlag: boolean;
};

export type InitializeFlags = {
  initializeHasDividendAccountFlag? : boolean;
  initializeHasValidInvoiceNumberAnswerFlag? : boolean;
  initializeHasValidFatcaAnswerFlag? : boolean;
}

const getHasFlag = ({ flag, status }: { flag?: boolean; status?: boolean | null }) => {
  return flag ? !!status : true;
};

export const useApplicationStatus = (
  navigate: () => void,
  options?: InitializeFlags,
) => {
  const [_status, setStatus] = useChangeDomesticRealEstateApplicationMutation();
  const { showLoading, hideLoading } = useGlobalLoadingHandler();
  const { showError, messageClose } = useGlobalToastHandler();
  const { isLogin } = useAuthGuard();
  const [accountInfo, _] = useGetAccountInfoQuery({
    pause: !isLogin,
    requestPolicy: 'network-only',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validation = Yup.object().shape({
    bankName: Yup.string()
      .nullable()
      .when('hasDividendAccountFlag', {
        is: false,
        then: (schema) => schema.required(ErrorWord.INPUT).max(255, getMaxLengthErrorMessage(255)),
        otherwise: (schema) => schema,
      }),

    branchName: Yup.string()
      .nullable()
      .when('hasDividendAccountFlag', {
        is: false,
        then: (schema) => schema.required(ErrorWord.INPUT).max(255, getMaxLengthErrorMessage(255)),
        otherwise: (schema) => schema,
      }),
    accountType: Yup.number()
      .nullable()
      .when('hasDividendAccountFlag', {
        is: false,
        then: (schema) => schema.not([AccountType.None, null], ErrorWord.SELECT),
        otherwise: (schema) => schema,
      }),
    accountNumber: Yup.string()
      .nullable()
      .when('hasDividendAccountFlag', {
        is: false,
        then: (schema) => schema.required(ErrorWord.INPUT).max(255, getMaxLengthErrorMessage(255)),
        otherwise: (schema) => schema,
      }),
    accountHolderName: Yup.string()
      .nullable()
      .when('hasDividendAccountFlag', {
        is: false,
        then: (schema) => schema.required(ErrorWord.INPUT).max(255, getMaxLengthErrorMessage(255)),
        otherwise: (schema) => schema,
      }),
    fatca: Yup.number()
      .nullable()
      .when('hasValidFatcaAnswerFlag', {
        is: false,
        then: (schema) =>
          schema
            .notOneOf([FatcaStatus.None, null], ErrorWord.SELECT)
            .test(
              'not-yes',
              '恐れ入りますが、口座開設はお受けできません。取引を希望される場合は最寄りの営業拠点にお問い合わせください。',
              (value) => value !== FatcaStatus.Yes,
            ),
        otherwise: (schema) => schema,
      }),
    invoiceNumber: Yup.string()
      .nullable()
      .when('hasValidInvoiceNumberAnswerFlag', {
        is: false,
        then: (schema) =>
          schema.when('hasNoInvoiceNumber', {
            is: false || null,
            then: () =>
              yupNumberString({
                length: 13,
                isRequired: true,
                errorWord: ErrorWord.INVOICE_NUMBER,
              }),
          }),
        otherwise: (schema) => schema.strip(),
      }),
  });

  const initialValues: ApplicationStatusType = {
    bankName: null,
    branchName: null,
    accountType: null,
    accountNumber: null,
    accountHolderName: null,
    fatca: null,
    invoiceNumber: null,
    hasNoInvoiceNumber: null,

    hasDividendAccountFlag: getHasFlag({
      flag: options?.initializeHasDividendAccountFlag,
      status: accountInfo.data?.GetAccountInfo?.ApplicationStatus?.HasAnsweredDividendAccount,
    }),
    hasValidFatcaAnswerFlag: getHasFlag({
      flag: options?.initializeHasValidFatcaAnswerFlag,
      status: accountInfo.data?.GetAccountInfo?.ApplicationStatus?.HasValidFatcaAnswer != null,
    }),
    hasValidInvoiceNumberAnswerFlag: getHasFlag({
      flag: options?.initializeHasValidInvoiceNumberAnswerFlag,
      status: accountInfo.data?.GetAccountInfo?.ApplicationStatus?.HasAnsweredInvoiceNumber,
    }),
  };

  const formik = useFormik<ApplicationStatusType>({
    initialValues,
    async onSubmit(input) {
      showLoading();
      //申込準備情報を更新
      const res = await setStatus({
        input: {
          TransferBankName: input.bankName,
          TransferBranchName: input.branchName,
          TransferAccountType: input.accountType,
          TransferAccountNumber: input.accountNumber,
          TransferAccountHolderName: input.accountHolderName,
          Fatca: input.fatca,
          InvoiceNumber: input.invoiceNumber,
          HasNoInvoiceNumber: input.hasNoInvoiceNumber,
        },
      });
      hideLoading();

      if (!res?.data) {
        return;
      }
      if (res?.data?.ChangeDomesticRealEstateApplication?.Code !== 0) {
        showError(res.data.ChangeDomesticRealEstateApplication?.Message);
        return;
      }

      navigate();
    },
    validationSchema: validation,
    validateOnChange: false,
    validateOnBlur: false,
    validate: () => {
      // バリデーションかける時に必ずエラートーストを閉じる
      messageClose();
    },
  });

  const bankDetailsInfoProps: any = {
    selectBankProps: {
      selected: formik.values.bankName,
      onSelectedBank: async (value: string) => {
        await formik.setFieldValue('bankName', value);
      },
      error: formik.errors.bankName,
    },
    selectBranchProps: {
      selected: formik.values.branchName,
      onSelectedBranch: async (value: string) => {
        await formik.setFieldValue('branchName', value);
      },
      error: formik.errors.branchName,
    },
    accountTypeProps: {
      selected: formik.values.accountType,
      onChange: async (value: string) => {
        await formik.setFieldValue('accountType', value);
      },
      error: formik.errors.accountType,
    },
    accountNumberProps: {
      value: formik.values.accountNumber,
      onChange: async (value: string) => {
        await formik.setFieldValue('accountNumber', value);
      },
      error: formik.errors.accountNumber,
    },
    accountHolderNameProps: {
      value: formik.values.accountHolderName,
      onChange: async (value: string) => {
        await formik.setFieldValue('accountHolderName', value);
      },
      error: formik.errors.accountHolderName,
    },
  };

  const fatcaProps: any = {
    selected: formik.values.fatca,
    onChange: async (value: FatcaStatus) => {
      await formik.setFieldValue('fatca', value);
      formik.validateField('fatca');
    },
    error: formik.errors.fatca,
  };
  const invoiceNumberProps: any = {
    textInput: {
      value: formik.values.invoiceNumber,
      onChange: (value: string) => {
        formik.setFieldValue('invoiceNumber', value)
        formik.setFieldValue('hasNoInvoiceNumber', false);
      },
      disabled: formik.values.hasNoInvoiceNumber,
      error: formik.errors.invoiceNumber,
    },
    checkInput: {
      checked: formik.values.hasNoInvoiceNumber,
      setChecked: (value: boolean) => {
        formik.setFieldValue('hasNoInvoiceNumber', value);
        if (value) {
          formik.setFieldValue('invoiceNumber', '');
        }
      },
      error: formik.errors.hasNoInvoiceNumber,
    },
  };

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    formik,
    bankDetailsInfoProps,
    fatcaProps,
    invoiceNumberProps,
    hasValidFatcaAnswer: formik.values.hasValidFatcaAnswerFlag,
    hasDividendAccount: formik.values.hasDividendAccountFlag,
    hasValidInvoiceNumberAnswer: formik.values.hasValidInvoiceNumberAnswerFlag,
    accountType: accountInfo.data?.GetAccountInfo?.AccountType,
  };
};
